// The four movement problems, as something a coach uses rather than reads.
//
// The masterclass argues that "is this a good passer" is the wrong question,
// and that the useful one is "WHICH version of the passing problem breaks
// them". A static 2x2 states that. This one makes the coach answer it: pick
// the quadrant where your player's accuracy falls apart and you get back what
// that particular breakdown means and what to train for it.
//
// Deliberately not scored and not persisted. There is no right answer to save,
// and a quiz that remembers your score turns a diagnostic into a test. The
// coach picks, reads, and picks again for the next player.
//
// No em dashes.

import React, { useState } from "react";
import { GOLD } from "../constants/theme";

/**
 * The four combinations, each with what a breakdown there actually means.
 *
 * The distinction that matters: 2 and 3 fail for completely different reasons,
 * and a coach who cannot tell them apart ends up giving both players the same
 * passing work. Leading a moving target is a timing problem. Passing on the
 * move is a body problem. Same missed pass, opposite fix.
 */
export const QUADRANTS = [
  {
    id: "ss",
    n: 1,
    passer: "Stationary",
    receiver: "Stationary",
    title: "Stationary to stationary",
    means:
      "The simplest version, and the one that reveals basic ball control. If accuracy breaks down here, nothing further up the list will hold, because every other version of the problem contains this one.",
    train: [
      "Precision reps to a defined target, not just a completed catch",
      "Different release points from the same spot: chest, overhead, push, hook",
      "Different ball paths to the same target: direct, lob, bounce",
      "Score the arrival, not the catch",
    ],
  },
  {
    id: "sm",
    n: 2,
    passer: "Stationary",
    receiver: "Moving",
    title: "Stationary to moving",
    means:
      "A timing problem, not a body problem. The player can control the ball, but they are throwing to where the receiver IS rather than where they are going to be. Watch for passes that arrive behind the cutter.",
    train: [
      "Lead passes to a receiver running the same cut at different speeds",
      "Vary the receiver's speed without telling the passer",
      "Cuts at different angles so the lead has to change, not just the pace",
      "Ask the receiver whether they had to break stride",
    ],
  },
  {
    id: "ms",
    n: 3,
    passer: "Moving",
    receiver: "Stationary",
    title: "Moving to stationary",
    means:
      "A body problem, not a timing problem. The target is not going anywhere, so what is breaking is the passer's ability to deliver accurately while managing their own momentum, balance and footwork.",
    train: [
      "Pass off the dribble at speed, both directions",
      "Pass while moving away from the target",
      "One hand, off either foot, immediately after the pickup",
      "Vary the pickup: high dribble, low dribble, ball outside the frame",
    ],
  },
  {
    id: "mm",
    n: 4,
    passer: "Moving",
    receiver: "Moving",
    title: "Moving to moving",
    means:
      "The closest version to the game, and the one most plays actually consist of. A player who holds up through 2 and 3 and breaks here is struggling to solve both problems at once rather than either of them alone.",
    train: [
      "Live two on zero into three on zero with both players moving",
      "Small sided games with a passing standard attached",
      "Add the defender on the passer, then on the receiver, one at a time",
      "Then take the constraint away and see whether it held",
    ],
  },
];

export function PassingMatrix({ C }) {
  const [picked, setPicked] = useState(null);
  const chosen = QUADRANTS.find(q => q.id === picked) || null;

  const cell = (q) => {
    const on = picked === q.id;
    return (
      <button
        key={q.id}
        type="button"
        onClick={() => setPicked(on ? null : q.id)}
        aria-pressed={on}
        style={{
          display: "flex", flexDirection: "column", gap: 4,
          padding: "13px 12px", minHeight: 78, textAlign: "left",
          borderRadius: 11, cursor: "pointer", fontFamily: "inherit",
          border: `1px solid ${on ? GOLD : C.border}`,
          background: on ? "rgba(226,221,159,0.12)" : (C.cardGradient || "rgba(255,255,255,0.02)"),
          transition: "border-color .15s, background .15s",
        }}
      >
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
          color: on ? GOLD : C.textDim,
        }}>{q.n}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.35, color: C.text }}>
          {q.passer} passer
        </span>
        <span style={{ fontSize: 12.5, lineHeight: 1.35, color: C.textMid || C.textDim }}>
          {q.receiver} receiver
        </span>
      </button>
    );
  };

  return (
    <div style={{ margin: "24px 0" }}>
      <div style={{
        fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase",
        color: C.textDim, marginBottom: 8,
      }}>
        Which one breaks your player
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {QUADRANTS.map(cell)}
      </div>

      {!chosen && (
        <div style={{
          marginTop: 10, fontSize: 12, lineHeight: 1.6, color: C.textDim,
        }}>
          Pick the version of the problem where their accuracy falls apart. Two and
          three look like the same missed pass and need opposite fixes.
        </div>
      )}

      {chosen && (
        <div style={{
          marginTop: 10, padding: "15px 16px", borderRadius: 12,
          border: `1px solid ${GOLD}44`, background: "rgba(226,221,159,0.06)",
        }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: GOLD, marginBottom: 6 }}>
            {chosen.title}
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.65, color: C.text, marginBottom: 12 }}>
            {chosen.means}
          </div>
          <div style={{
            fontSize: 10, fontWeight: 800, letterSpacing: 0.9, textTransform: "uppercase",
            color: C.textDim, marginBottom: 6,
          }}>
            What to train
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 5 }}>
            {chosen.train.map((t, i) => (
              <li key={i} style={{ fontSize: 13, lineHeight: 1.6, color: C.textMid || C.text }}>
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 11.5, lineHeight: 1.6, color: C.textDim }}>
        A player can sit in a different square for different passes. The question is
        worth asking per player and per situation, not once.
      </div>
    </div>
  );
}

export default PassingMatrix;
